<template>
  <div class="h-full">
    <!-- Compact Mode -->
    <div v-if="!expanded" class="h-full flex flex-col">
      <div>
        <p class="text-4xl font-bold text-primary-600">{{ compactSummary.coveragePercent }}%</p>
        <p class="text-sm text-gray-600">practices engaged</p>
        <p class="text-xs text-gray-400 mt-1">{{ filterDescription }}</p>
      </div>
      <!-- Compact Chart - taller when fullWidth -->
      <div v-if="chartData" class="flex-1 min-h-[100px]">
        <ChartV4
          :key="`compact-chart-${fullWidth}`"
          :chart_data="chartData"
          :chart_config="fullWidth ? fullWidthCompactChartConfig : compactChartConfig"
          :overrideHeight="fullWidth ? 'h-[160px]' : 'h-[100px]'"
        />
      </div>
      <div v-else-if="loading" class="flex-1 flex items-center justify-center">
        <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    </div>

    <!-- Expanded Mode -->
    <div v-else>
    <!-- Tier 1: Primary Controls -->
    <div class="flex flex-wrap items-center gap-4 mb-4">
      <!-- Scope Toggle -->
      <div class="flex bg-gray-100 rounded-lg p-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="portfolioFilter = tab.id"
          :class="[
            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            portfolioFilter === tab.id
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          {{ tab.name }}
        </button>
      </div>

      <!-- Entity Scope (when All Practices selected) -->
      <div v-if="scopeEntityOptions.length > 0" class="flex items-center gap-2">
        <select
          v-model="selectedEntityId"
          @change="fetchCoverageData"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-600"
        >
          <option :value="null">All Entities</option>
          <optgroup v-for="type in uniqueEntityTypes" :key="type" :label="type">
            <option v-for="entity in scopeEntityOptions.filter(e => e.type === type)" :key="entity.id" :value="entity.id" class="sensitive" data-spoof-kind="practice">
              {{ entity.name }}
            </option>
          </optgroup>
        </select>
      </div>

      <!-- Time Range Toggle -->
      <div class="flex bg-gray-100 rounded-lg p-1">
        <button
          v-for="range in timeRangeOptions"
          :key="range.value"
          @click="setTimeRange(range.value)"
          :class="[
            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            activeTimeRange === range.value
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          {{ range.label }}
        </button>
      </div>

      <!-- Practice Tags (inline grouped filter) -->
      <GroupedInfoTagFilter
        v-model="infoTagGroups"
        v-model:groupsMode="infoTagGroupsMode"
        label="Filter by Practice Tag"
        buttonLabel="Tag"
        color="emerald"
        direction="horizontal"
        @change="fetchCoverageData"
      />

      <!-- Advanced Toggle -->
      <button
        @click="showAdvanced = !showAdvanced"
        :class="[
          'p-2 rounded-lg transition-colors',
          showAdvanced ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100 text-gray-500'
        ]"
        title="Advanced filters"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
      </button>

    </div>

    <!-- Tier 2: Active Filter Chips -->
    <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mb-4">
      <Tag
        v-if="statusFilterChanged"
        :label="`Status: ${selectedStatuses.map(s => statusOptions.find(o => o.value === s)?.label || s).join(', ')}`"
        color="primary"
        :removable="true"
        @remove="selectedStatuses = ['COMPLETED', 'SCHEDULED']; fetchCoverageData()"
      />
      <Tag
        v-if="selectedUserRoles.length > 0"
        :label="`Roles: ${selectedUserRoles.join(', ')}`"
        color="primary"
        :removable="true"
        @remove="selectedUserRoles = []; fetchCoverageData()"
      />
      <Tag
        v-if="selectedContracts.length > 0"
        :label="`Contracts: ${selectedContracts.length}`"
        color="primary"
        :removable="true"
        @remove="selectedContracts = []; fetchCoverageData()"
      />
      <Tag
        v-if="selectedEngagementChannels.length > 0"
        :label="`Channels: ${selectedEngagementChannels.length}`"
        color="primary"
        :removable="true"
        @remove="selectedEngagementChannels = []; fetchCoverageData()"
      />
      <Tag
        v-if="selectedEngagementTypes.length > 0"
        :label="`Types: ${selectedEngagementTypes.length}`"
        color="purple"
        :removable="true"
        @remove="selectedEngagementTypes = []; fetchCoverageData()"
      />
      <Tag
        v-if="selectedEngagementTopics.length > 0"
        :label="`Topics: ${selectedEngagementTopics.length}`"
        color="purple"
        :removable="true"
        @remove="selectedEngagementTopics = []; fetchCoverageData()"
      />
      <Tag
        v-if="selectedEntityId"
        :label="`Scope: ${scopeEntityOptions.find(e => e.id === selectedEntityId)?.name}`"
        color="emerald"
        :removable="true"
        @remove="selectedEntityId = null; fetchCoverageData()"
      />
      <Tag
        v-if="filters.group_by !== 'total'"
        :label="`Group: ${groupByOptions.find(o => o.value === filters.group_by)?.label}`"
        color="gray"
        :removable="true"
        @remove="filters.group_by = 'total'; fetchCoverageData()"
      />
      <Tag
        v-if="filters.coverage_metric !== 'entity'"
        label="Attribution %"
        color="gray"
        :removable="true"
        @remove="filters.coverage_metric = 'entity'; fetchCoverageData()"
      />
      <button
        @click="clearAllFilters"
        class="text-sm text-gray-500 hover:text-gray-700 ml-2"
      >
        Clear all
      </button>
    </div>

    <!-- Tier 3: Advanced Panel -->
    <div v-if="showAdvanced" ref="advancedPanel" class="p-4 bg-gray-50 rounded-lg mb-4 border border-gray-200">
      <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
        <!-- Status -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Count Statuses</label>
          <div class="space-y-1">
            <label v-for="opt in statusOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedStatuses" @change="fetchCoverageData" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- Channel -->
        <div v-if="engagementChannelOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Channel</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in engagementChannelOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedEngagementChannels" @change="fetchCoverageData" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- User Role -->
        <div v-if="userRoleOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">User Role</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in userRoleOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedUserRoles" @change="fetchCoverageData" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- Engagement Type -->
        <div v-if="engagementTypeOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Engagement Type</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in engagementTypeOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedEngagementTypes" @change="fetchCoverageData" class="mr-2 text-purple-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- Engagement Topic -->
        <div v-if="engagementTopicOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Interaction Topic</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in engagementTopicOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedEngagementTopics" @change="fetchCoverageData" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>
      </div>

      <!-- Display Controls -->
      <div class="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-6">
        <!-- Group By -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Group By</label>
          <select
            v-model="filters.group_by"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
            @change="fetchCoverageData"
          >
            <option v-for="option in groupByOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Coverage Metric -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Coverage Metric</label>
          <div class="space-y-1">
            <label class="flex items-center text-sm cursor-pointer">
              <input type="radio" v-model="filters.coverage_metric" value="entity" @change="fetchCoverageData" class="mr-2 text-primary-600">
              Practice %
            </label>
            <label class="flex items-center text-sm cursor-pointer">
              <input type="radio" v-model="filters.coverage_metric" value="attribution" @change="fetchCoverageData" class="mr-2 text-primary-600">
              Attribution %
            </label>
          </div>
        </div>

        <!-- Observation Frequency -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Chart Frequency</label>
          <select
            v-model="filters.observation_granularity"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
            @change="fetchCoverageData"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </div>

        <!-- Observation Period -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Observation Period</label>
          <div class="flex items-center gap-2">
            <input
              v-model="filters.start_date"
              type="date"
              class="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
              @change="fetchCoverageData"
            />
            <span class="text-gray-500">to</span>
            <input
              v-model="filters.end_date"
              type="date"
              class="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
              @change="fetchCoverageData"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <LoadingIndicator v-if="loading" message="Loading coverage data..." />

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-600">{{ error }}</p>
    </div>

    <!-- Coverage Summary (simplified) -->
    <div v-else-if="coverageData" class="space-y-6">
      <!-- Key Metrics -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="text-center p-4 bg-gray-50 rounded-lg">
          <p class="text-3xl font-bold text-primary-600">{{ coverageData.summary.entity_coverage_percent }}%</p>
          <p class="text-sm text-gray-500">practice coverage</p>
        </div>
        <div class="text-center p-4 bg-gray-50 rounded-lg">
          <p class="text-3xl font-bold text-gray-900">{{ coverageData.summary.entities_with_engagement }}</p>
          <p class="text-sm text-gray-500">of {{ coverageData.summary.total_entities }} practices</p>
        </div>
        <div class="text-center p-4 bg-gray-50 rounded-lg">
          <p class="text-3xl font-bold text-emerald-600">{{ coverageData.summary.attribution_coverage_percent }}%</p>
          <p class="text-sm text-gray-500">attribution coverage</p>
        </div>
        <div class="text-center p-4 bg-gray-50 rounded-lg">
          <p class="text-3xl font-bold text-gray-900">{{ coverageData.summary.attribution_with_engagement?.toLocaleString() }}</p>
          <p class="text-sm text-gray-500">of {{ coverageData.summary.total_attribution?.toLocaleString() }} patients</p>
        </div>
      </div>

      <!-- Coverage Chart -->
      <div class="mt-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ getChartTitle() }}
          </h3>
        </div>

        <div v-if="chartData" class="bg-white rounded-lg border border-gray-200 p-4" style="height: 500px;">
          <ChartV4
            :key="`chart-${filters.coverage_metric}-${filters.group_by}-${filters.observation_granularity}-${filters.lookback_window}-${selectedUserRoles.join(',')}-${fullWidth}`"
            :chart_data="chartData"
            :chart_config="chartConfig"
            :overrideHeight="'h-[500px]'"
          />
        </div>
        <div v-else class="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
          <p class="text-gray-500">Loading chart data...</p>
        </div>
      </div>

      <!-- Group By Legend/Key -->
      <div v-if="filters.group_by !== 'total' && chartData && chartData.series" class="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">
          {{ groupByOptions.find(o => o.value === filters.group_by)?.label || 'Group' }} Breakdown
        </h4>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          <div
            v-for="series in chartData.series"
            :key="series.config.label"
            class="flex items-center gap-2 p-2 bg-white rounded border border-gray-200"
          >
            <div
              class="w-4 h-4 rounded shrink-0"
              :style="{ backgroundColor: series.config.backgroundColor }"
            ></div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 truncate" :title="series.config.label">
                {{ series.config.label }}
              </div>
              <div class="text-xs text-gray-500">
                {{ Math.round(series.values[series.values.length - 1] || 0) }}% latest
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Practice Data Section -->
      <div v-if="coverageData && coverageData.practice_details" class="mt-6">
        <div class="flex items-center gap-3">
          <button
            @click="showPracticeData = !showPracticeData"
            class="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            <svg
              :class="['w-4 h-4 transition-transform', showPracticeData ? 'rotate-90' : '']"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
            <span class="text-sm font-medium">Practice Coverage Details</span>
          </button>

          <ExportMenu v-if="showPracticeData" class="ml-auto" :exports="[{ type: 'excel', action: exportToExcel }]" />
        </div>

        <div v-if="showPracticeData" class="mt-4 space-y-4">
          <!-- Practices Counted -->
          <div class="border rounded-lg overflow-hidden">
            <div class="bg-emerald-50 px-4 py-2 border-b flex items-center justify-between">
              <h4 class="text-sm font-semibold text-emerald-800">
                Practices Counted ({{ coverageData.practice_details.practices_counted?.length || 0 }} engagements)
              </h4>
            </div>
            <div class="max-h-64 overflow-y-auto">
              <table v-if="coverageData.practice_details.practices_counted?.length" class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entity Name</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white">
                  <tr
                    v-for="row in coverageData.practice_details.practices_counted"
                    :key="row.engagement_id"
                    class="hover:bg-gray-50"
                  >
                    <td class="px-4 py-2 text-sm">
                      <span
                        class="sensitive text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                        @click="navigateToEntity(row.entity_id)"
                      >
                        {{ row.entity_name }}
                      </span>
                    </td>
                    <td class="px-4 py-2 text-sm text-gray-600">{{ formatDate(row.date) }}</td>
                    <td class="px-4 py-2 text-sm text-gray-600 max-w-xs truncate sensitive" :title="row.subject">{{ row.subject || '—' }}</td>
                    <td class="px-4 py-2 text-sm text-gray-600">{{ row.channel || '—' }}</td>
                    <td class="px-4 py-2 text-sm text-gray-600">{{ row.type || '—' }}</td>
                    <td class="px-4 py-2 text-sm">
                      <span :class="[
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        row.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' :
                        row.status === 'SCHEDULED' ? 'bg-blue-100 text-blue-800' :
                        row.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      ]">
                        {{ row.status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-4 text-center text-gray-500 text-sm">
                No practices counted in this time period
              </div>
            </div>
          </div>

          <!-- Practices NOT Counted -->
          <div class="border rounded-lg overflow-hidden">
            <div class="bg-red-50 px-4 py-2 border-b">
              <h4 class="text-sm font-semibold text-red-800">
                Practices NOT Counted ({{ coverageData.practice_details.practices_not_counted?.length || 0 }} practices)
              </h4>
            </div>
            <div class="max-h-64 overflow-y-auto">
              <table v-if="coverageData.practice_details.practices_not_counted?.length" class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entity Name</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Engagement</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Days Since</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white">
                  <tr
                    v-for="row in coverageData.practice_details.practices_not_counted"
                    :key="row.entity_id"
                    class="hover:bg-gray-50 cursor-pointer"
                    @click="navigateToEntity(row.entity_id)"
                  >
                    <td class="sensitive px-4 py-2 text-sm text-primary-600 hover:text-primary-800 hover:underline">
                      {{ row.entity_name }}
                    </td>
                    <td class="px-4 py-2 text-sm text-gray-600">
                      {{ row.last_engagement_date ? formatDate(row.last_engagement_date) : 'Never' }}
                    </td>
                    <td class="px-4 py-2 text-sm">
                      <span v-if="row.days_since_engagement !== null" :class="[
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        row.days_since_engagement > 90 ? 'bg-red-100 text-red-800' :
                        row.days_since_engagement > 30 ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-800'
                      ]">
                        {{ row.days_since_engagement }} days
                      </span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-4 text-center text-gray-500 text-sm">
                All practices have qualifying engagements
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div><!-- /expanded -->
  </div>
</template>

<script>
import axios from 'axios'
import ChartV4 from '@/components/widgetcomponents/Viz_Elements/Chart_V4.vue'
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue'
import GroupedInfoTagFilter from '@/components/widgetcomponents/Dropdown_Selector/GroupedInfoTagFilter.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { exportToExcel } from '@/utils/exportArraytoXlsx.js'
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';

export default {
  name: 'EngagementCoverage',
  components: {
    ChartV4,
    SimpleMultiSelect,
    GroupedInfoTagFilter,
    LoadingIndicator,
    Tag,
    ExportMenu
  },
  props: {
    settings: {
      type: Object,
      default: () => ({})
    },
    sectionId: {
      type: String,
      default: null
    },
    expanded: {
      type: Boolean,
      default: true
    },
    fullWidth: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      coverageData: null,
      chartData: null,
      showPracticeData: false,
      selectedStatuses: ['COMPLETED', 'SCHEDULED'],
      selectedContracts: [],
      contractOptions: [],
      selectedEngagementChannels: [],
      engagementChannelOptions: [],
      selectedEntityId: null,
      scopeEntityOptions: [],
      selectedUserRoles: [],
      portfolioFilter: undefined, // 'all' or 'my', set on mounted to first available tab
      showAdvanced: false,
      activeTimeRange: '30d',
      timeRangeOptions: [
        { value: '7d', label: '7d' },
        { value: '30d', label: '30d' },
        { value: '90d', label: '90d' },
        { value: 'ytd', label: 'YTD' }
      ],
      filters: {
        observation_granularity: 'week',
        start_date: this.getDefaultStartDate(),
        end_date: this.getDefaultEndDate(),
        entity_type_name: 'Practice',
        coverage_metric: 'entity',
        group_by: 'total'
      },
      statusOptions: [
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'SCHEDULED', label: 'Scheduled' },
        { value: 'CANCELLED', label: 'Cancelled' },
        { value: 'NO_SHOW', label: 'No Show' }
      ],
      userRoleOptions: [],
      engagementTypeOptions: [],
      selectedEngagementTypes: [],
      engagementTopicOptions: [],
      selectedEngagementTopics: [],
      // Grouped InfoTag filter state — array of {include, mode, exclude}
      // combined per `infoTagGroupsMode` (default 'or'). Empty array = no filter.
      infoTagGroups: [],
      infoTagGroupsMode: 'or',
      infoTagOptions: [],
      groupByOptions: [
        { value: 'total', label: 'Total Only' },
        { value: 'user_role', label: 'By Role' },
        { value: 'status', label: 'By Status' },
        { value: 'engagement_type', label: 'By Type' },
        { value: 'appuser', label: 'By User' },
        { value: 'has_transcript', label: 'By Transcript' },
        { value: 'has_notes', label: 'By Notes' },
        { value: 'engagement_channel', label: 'By Channel' },
        { value: 'engagement_topic', label: 'By Topic' }
      ],
      colorPalettes: {
        status: {
          'COMPLETED': '#10B981',
          'SCHEDULED': '#3B82F6',
          'CANCELLED': '#EF4444',
          'NO_SHOW': '#F59E0B'
        },
        has_transcript: { 'Yes': '#10B981', 'No': '#EF4444' },
        has_notes: { 'Yes': '#10B981', 'No': '#EF4444' }
      },
      chartConfig: {
        chartType: 'vertical_bar',
        title: 'Practice Coverage Over Time',
        margin: { top: 80, right: 60, bottom: 120, left: 80 },
        xAxis: {
          labelRotation: -45,
          fontSize: 12,
          tickFormat: null
        },
        yAxes: [
          {
            position: 'left',
            fontSize: 12,
            tickFormat: (d) => `${d}%`
          }
        ],
        legend: {
          display: false,
          position: 'right',
          spacing: 15
        },
        formatting: {
          percentage: false,
          sig_figs: 1
        },
        labelDisplayConfig: {
          maxLabels: 40,
          minLabelSpacing: 5,
          labelFontSize: 12
        }
      },
      compactChartConfig: {
        chartType: 'vertical_bar',
        title: '',
        margin: { top: 5, right: 5, bottom: 25, left: 35 },
        xAxis: {
          labelRotation: 0,
          fontSize: 8
        },
        yAxes: [{
          position: 'left',
          fontSize: 8,
          tickFormat: (d) => `${d}%`
        }],
        legend: { display: false },
        formatting: { percentage: false, sig_figs: 0 },
        labelDisplayConfig: { maxLabels: 8, minLabelSpacing: 5, labelFontSize: 8 }
      },
      fullWidthCompactChartConfig: {
        chartType: 'vertical_bar',
        title: '',
        margin: { top: 10, right: 20, bottom: 40, left: 50 },
        xAxis: {
          labelRotation: -45,
          fontSize: 10
        },
        yAxes: [{
          position: 'left',
          fontSize: 10,
          tickFormat: (d) => `${d}%`
        }],
        legend: { display: false },
        formatting: { percentage: false, sig_figs: 1 },
        labelDisplayConfig: { maxLabels: 30, minLabelSpacing: 5, labelFontSize: 10 }
      }
    }
  },
  computed: {
    isMyPractices() {
      return this.portfolioFilter === 'my'
    },
    tabs() {
      return [
        ...(canAccessFeature(FEATURES.COMPONENT_PRACTICE_PARTNER_ALL)
          ? [{ id: 'all', name: 'All Practices' }]
          : []),
        {
          id: 'my',
          name: 'My Practices'
        }
      ]
    },
    compactSummary() {
      if (!this.coverageData?.summary) {
        return { coveragePercent: '—' }
      }
      return {
        coveragePercent: this.coverageData.summary.entity_coverage_percent
      }
    },
    filterPersistKey() {
      const tenantSlug = this.$store.getters['tenant/current_tenant_slug'] || 'default'
      return `${tenantSlug}_${this.settings.persist_key || 'engagement_coverage'}_filters`
    },
    filterDescription() {
      const parts = []

      // Portfolio scope
      if (this.portfolioFilter === 'my') {
        parts.push('My practices')
      } else {
        parts.push('All practices')
      }

      // InfoTag group filters (Implementation Status)
      const infoTagSummary = this.infoTagGroupsSummary
      if (infoTagSummary) {
        parts.push(infoTagSummary)
      }

      // Date range - derive label from actual dates
      const startDate = new Date(this.filters.start_date)
      const endDate = new Date(this.filters.end_date)
      const daysDiff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
      if (daysDiff <= 7) {
        parts.push('Last 7 days')
      } else if (daysDiff <= 30) {
        parts.push('Last 30 days')
      } else if (daysDiff <= 90) {
        parts.push('Last 90 days')
      } else {
        parts.push('Year to date')
      }

      // Advanced filters
      const defaultStatuses = ['COMPLETED', 'SCHEDULED']
      const statusChanged = this.selectedStatuses.length !== defaultStatuses.length ||
        !defaultStatuses.every(s => this.selectedStatuses.includes(s))
      if (statusChanged) {
        const labels = this.selectedStatuses.map(s => this.statusOptions.find(o => o.value === s)?.label || s)
        parts.push('Status: ' + labels.join(', '))
      }
      if (this.selectedUserRoles.length > 0) {
        parts.push('Roles: ' + this.selectedUserRoles.join(', '))
      }
      if (this.selectedEngagementChannels.length > 0) {
        parts.push(this.selectedEngagementChannels.length + ' channel' + (this.selectedEngagementChannels.length > 1 ? 's' : ''))
      }
      if (this.selectedContracts.length > 0) {
        parts.push(this.selectedContracts.length + ' contract' + (this.selectedContracts.length > 1 ? 's' : ''))
      }
      if (this.selectedEngagementTypes.length > 0) {
        parts.push(this.selectedEngagementTypes.length + ' type' + (this.selectedEngagementTypes.length > 1 ? 's' : ''))
      }
      if (this.selectedEntityId) {
        const entity = this.scopeEntityOptions.find(e => e.id === this.selectedEntityId)
        if (entity) parts.push('Scope: ' + entity.name)
      }
      if (this.filters.group_by !== 'total') {
        const groupLabel = this.groupByOptions.find(o => o.value === this.filters.group_by)?.label
        if (groupLabel) parts.push(groupLabel)
      }
      if (this.filters.coverage_metric !== 'entity') {
        parts.push('Attribution %')
      }

      return parts.join(' · ')
    },
    statusFilterChanged() {
      const defaults = ['COMPLETED', 'SCHEDULED']
      return this.selectedStatuses.length !== defaults.length ||
        !defaults.every(s => this.selectedStatuses.includes(s))
    },
    hasActiveFilters() {
      return this.statusFilterChanged ||
             this.selectedUserRoles.length > 0 ||
             this.selectedContracts.length > 0 ||
             this.selectedEngagementChannels.length > 0 ||
             this.selectedEngagementTypes.length > 0 ||
             this.selectedEngagementTopics.length > 0 ||
             this.activeInfoTagGroups.length > 0 ||
             this.selectedEntityId !== null ||
             this.filters.group_by !== 'total' ||
             this.filters.coverage_metric !== 'entity'
    },
    activeInfoTagGroups() {
      return (this.infoTagGroups || []).filter(g =>
        (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
      )
    },
    infoTagGroupsSummary() {
      const groups = this.activeInfoTagGroups
      if (groups.length === 0) return ''
      const tagName = (id) => this.infoTagOptions.find(t => t.id === id)?.name || id
      const groupSummaries = groups.map(g => {
        const inc = (g.include || []).map(tagName)
        const exc = (g.exclude || []).map(t => `NOT ${tagName(t)}`)
        const joiner = g.mode === 'and' ? ' AND ' : ' OR '
        return [...inc, ...exc].join(joiner)
      })
      if (groupSummaries.length === 1) return groupSummaries[0]
      const between = this.infoTagGroupsMode === 'and' ? ' AND ' : ' OR '
      return groupSummaries.map(s => `(${s})`).join(between)
    },
    uniqueEntityTypes() {
      return [...new Set(this.scopeEntityOptions.map(e => e.type))]
    }
  },
  methods: {
    applyTimeRange(preset) {
      // Calculate dates from preset without fetching data
      this.activeTimeRange = preset
      const end = new Date()
      let start = new Date()

      switch(preset) {
        case '7d':
          start.setDate(end.getDate() - 7)
          this.filters.observation_granularity = 'day'
          break
        case '30d':
          start.setDate(end.getDate() - 30)
          this.filters.observation_granularity = 'day'
          break
        case '90d':
          start.setDate(end.getDate() - 90)
          this.filters.observation_granularity = 'week'
          break
        case 'ytd':
          start = new Date(end.getFullYear(), 0, 1)
          this.filters.observation_granularity = 'week'
          break
      }

      this.filters.start_date = start.toISOString().split('T')[0]
      this.filters.end_date = end.toISOString().split('T')[0]
    },

    setTimeRange(preset) {
      this.applyTimeRange(preset)
      this.fetchCoverageData()
    },

    clearAllFilters() {
      this.selectedUserRoles = []
      this.selectedContracts = []
      this.selectedEngagementChannels = []
      this.selectedEngagementTypes = []
      this.selectedEngagementTopics = []
      this.infoTagGroups = []
      this.infoTagGroupsMode = 'or'
      this.selectedEntityId = null
      this.filters.group_by = 'total'
      this.filters.coverage_metric = 'entity'
      this.fetchCoverageData()
    },

    saveFilters() {
      const filterState = {
        filters: this.filters,
        activeTimeRange: this.activeTimeRange,
        selectedStatuses: this.selectedStatuses,
        selectedContracts: this.selectedContracts,
        selectedEngagementChannels: this.selectedEngagementChannels,
        selectedEngagementTypes: this.selectedEngagementTypes,
        selectedEngagementTopics: this.selectedEngagementTopics,
        infoTagGroups: this.infoTagGroups,
        infoTagGroupsMode: this.infoTagGroupsMode,
        selectedEntityId: this.selectedEntityId,
        selectedUserRoles: this.selectedUserRoles,
        portfolioFilter: this.portfolioFilter,
        groupBy: this.filters.group_by
      }
      localStorage.setItem(this.filterPersistKey, JSON.stringify(filterState))
    },

    loadFilters() {
      // 1. Start with widget defaults (already set in data())

      // 2. Apply baseline from page config (settings.default_filters)
      if (this.settings.default_filters) {
        this.filters = { ...this.filters, ...this.settings.default_filters }
        if (this.settings.default_filters.infoTagGroups) {
          this.infoTagGroups = this.settings.default_filters.infoTagGroups
        } else if (this.settings.default_filters.selectedInfoTags) {
          // Legacy default_filters: translate flat list into a single group
          this.infoTagGroups = [{
            include: this.settings.default_filters.selectedInfoTags,
            mode: this.settings.default_filters.infoTagFilterMode || 'or',
            exclude: this.settings.default_filters.excludedInfoTags || [],
          }]
        }
        if (this.settings.default_filters.infoTagGroupsMode) {
          this.infoTagGroupsMode = this.settings.default_filters.infoTagGroupsMode
        }
        if (this.settings.default_filters.selectedEngagementTypes) {
          this.selectedEngagementTypes = this.settings.default_filters.selectedEngagementTypes
        }
      }

      // 3. Apply localStorage user overrides (if any)
      const stored = localStorage.getItem(this.filterPersistKey)
      if (stored) {
        try {
          const filterState = JSON.parse(stored)
          if (filterState.activeTimeRange) this.activeTimeRange = filterState.activeTimeRange
          if (filterState.selectedStatuses) this.selectedStatuses = filterState.selectedStatuses
          if (filterState.selectedContracts) this.selectedContracts = filterState.selectedContracts
          if (filterState.selectedEngagementChannels) this.selectedEngagementChannels = filterState.selectedEngagementChannels
          if (filterState.selectedEngagementTypes) this.selectedEngagementTypes = filterState.selectedEngagementTypes
          if (filterState.selectedEngagementTopics) this.selectedEngagementTopics = filterState.selectedEngagementTopics
          // InfoTag filter — prefer new grouped shape, migrate from legacy flat keys.
          if (filterState.infoTagGroups) {
            this.infoTagGroups = filterState.infoTagGroups
          } else if (
            (filterState.selectedInfoTags?.length || 0) > 0 ||
            (filterState.excludedInfoTags?.length || 0) > 0
          ) {
            this.infoTagGroups = [{
              include: filterState.selectedInfoTags || [],
              mode: filterState.infoTagFilterMode || 'or',
              exclude: filterState.excludedInfoTags || [],
            }]
          }
          if (filterState.infoTagGroupsMode === 'and' || filterState.infoTagGroupsMode === 'or') {
            this.infoTagGroupsMode = filterState.infoTagGroupsMode
          }
          if (filterState.selectedEntityId !== undefined) this.selectedEntityId = filterState.selectedEntityId
          if (filterState.selectedUserRoles) this.selectedUserRoles = filterState.selectedUserRoles
          if (filterState.portfolioFilter) this.portfolioFilter = filterState.portfolioFilter
          if (filterState.groupBy) this.filters.group_by = filterState.groupBy
          // Recalculate dates from activeTimeRange to ensure consistency
          if (filterState.activeTimeRange) {
            this.applyTimeRange(filterState.activeTimeRange)
          }
        } catch (e) {
          console.warn('Failed to load saved filters:', e)
        }
      }
    },

    resetToDefaults() {
      // Clear localStorage for this widget
      localStorage.removeItem(this.filterPersistKey)
      // Reset to widget defaults
      this.activeTimeRange = '30d'
      this.filters = {
        observation_granularity: 'day',
        start_date: this.getDefaultStartDate(),
        end_date: this.getDefaultEndDate(),
        entity_type_name: 'Practice',
        coverage_metric: 'entity',
        group_by: 'total'
      }
      this.selectedUserRoles = []
      this.selectedEngagementTypes = []
      this.infoTagGroups = []
      this.infoTagGroupsMode = 'or'
      // Apply baseline from page config if present
      if (this.settings.default_filters) {
        if (this.settings.default_filters.activeTimeRange) {
          this.applyTimeRange(this.settings.default_filters.activeTimeRange)
        }
        if (this.settings.default_filters.infoTagGroupsMode) {
          this.infoTagGroupsMode = this.settings.default_filters.infoTagGroupsMode
        }
        if (this.settings.default_filters.infoTagGroups) {
          this.infoTagGroups = this.settings.default_filters.infoTagGroups
        } else if (this.settings.default_filters.selectedInfoTags) {
          this.infoTagGroups = [{
            include: this.settings.default_filters.selectedInfoTags,
            mode: this.settings.default_filters.infoTagFilterMode || 'or',
            exclude: this.settings.default_filters.excludedInfoTags || [],
          }]
        }
        if (this.settings.default_filters.selectedEngagementTypes) {
          this.selectedEngagementTypes = this.settings.default_filters.selectedEngagementTypes
        }
      }
      this.fetchCoverageData()
    },

    getDefaultStartDate() {
      // Default to 30 days ago to match default activeTimeRange of '30d'
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },

    getDefaultEndDate() {
      return new Date().toISOString().split('T')[0]
    },

    getLookbackLabel() {
      const labels = {
        'day': 'Daily',
        'week': 'Weekly',
        'month': 'Monthly'
      }
      return labels[this.filters.observation_granularity] || 'Unknown'
    },

    getChartTitle() {
      const metric = this.filters.coverage_metric === 'entity'
        ? 'Practice Coverage Over Time'
        : 'Attribution Coverage Over Time'

      const groupLabels = {
        'total': '',
        'user_role': ' by Role',
        'status': ' by Status',
        'engagement_type': ' by Type',
        'appuser': ' by User',
        'has_transcript': ' by Transcript',
        'has_notes': ' by Notes',
        'engagement_channel': ' by Channel'
      }

      const groupSuffix = groupLabels[this.filters.group_by] || ''

      const roleFilter = this.selectedUserRoles.length > 0
        ? ` (${this.selectedUserRoles.join(', ')} only)`
        : ''

      return metric + groupSuffix + roleFilter
    },

    async fetchUserRoleOptions() {
      try {
        const response = await axios.get('/api/entity-types/', {
          params: {
            is_user_role: true
          }
        })
        if (response.data) {
          this.userRoleOptions = response.data.map(entityType => ({
            value: entityType.type_name,
            label: entityType.type_name
          }))
          // Always add "Other" option at the end
          this.userRoleOptions.push({ value: 'Other', label: 'Other' })
        }
      } catch (error) {
        console.error('Error fetching user role options:', error)
        // If API fails or returns empty, don't show user role filter
        this.userRoleOptions = []
      }
    },

    async fetchContracts() {
      try {
        const typesRes = await axios.get('/api/entity-types/')
        const types = typesRes.data.results || typesRes.data || []
        const contractType = types.find(t => t.type_name === 'Contract' || t.type_name === 'Contract Pool')
        if (!contractType) return
        const response = await axios.get('/api/entities/', {
          params: {
            entity_type_id: contractType.id,
          }
        })
        if (response.data) {
          this.contractOptions = response.data.map(contract => ({
            value: contract.id.toString(),
            label: contract.name
          }))
        }
      } catch (error) {
        console.error('Error fetching contracts:', error)
        this.contractOptions = []
      }
    },

    async fetchEngagementChannels() {
      try {
        const response = await axios.get('/api/engagementChannels/')
        if (response.data) {
          this.engagementChannelOptions = response.data.map(channel => ({
            value: channel.id.toString(),
            label: channel.name
          }))
        }
      } catch (error) {
        console.error('Error fetching engagement channels:', error)
        this.engagementChannelOptions = []
      }
    },

    async fetchScopeEntities() {
      try {
        const params = {
          exclude_type: 'Practice',
          has_practice_children: 'true',
        }
        if (this.isMyPractices) {
          const currentUser = this.$store.state.auth.current_user
          if (currentUser && currentUser.id) {
            params.portfolio_user_id = currentUser.id
          }
        }
        const response = await axios.get('/api/entities/', { params })
        if (response.data) {
          this.scopeEntityOptions = response.data
            .map(entity => ({
              id: entity.id,
              name: entity.name,
              type: entity.entity_type_name
            }))
            .filter(e => e.type !== 'Contract Cohort')
            .sort((a, b) => {
              if (a.type !== b.type) return a.type.localeCompare(b.type)
              return a.name.localeCompare(b.name)
            })
        }
      } catch (error) {
        console.error('Error fetching scope entities:', error)
        this.scopeEntityOptions = []
      }
    },

    async fetchEngagementTypes() {
      try {
        const response = await axios.get('/api/engagementtypes/')
        if (response.data) {
          this.engagementTypeOptions = response.data.map(type => ({
            value: type.id,
            label: type.name
          }))
        }
      } catch (error) {
        console.error('Error fetching engagement types:', error)
        this.engagementTypeOptions = []
      }
    },

    async fetchEngagementTopics() {
      try {
        const response = await axios.get('/api/engagementtopics/')
        if (response.data) {
          this.engagementTopicOptions = response.data.map(topic => ({
            value: topic.id,
            label: topic.name
          }))
        }
      } catch (error) {
        console.error('Error fetching engagement topics:', error)
        this.engagementTopicOptions = []
      }
    },

    async fetchInfoTags() {
      try {
        const response = await axios.get('/api/infotags/', {
          params: { infoTagType__name: 'Implementation Status' }
        })
        if (response.data) {
          this.infoTagOptions = response.data.map(tag => ({
            id: tag.id,
            name: tag.name
          }))
        }
      } catch (error) {
        console.error('Error fetching info tags:', error)
        this.infoTagOptions = []
      }
    },

    async fetchCoverageData() {
      this.loading = true
      this.error = null

      try {
        const params = {
          entity_type_name: this.filters.entity_type_name,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          observation_granularity: this.filters.observation_granularity,
          include_practice_details: 'true',
          group_by: this.filters.group_by
        }

        if (this.selectedStatuses.length > 0) {
          params.engagement_status_filter = this.selectedStatuses.join(',')
        }

        // Add user role filter if selected
        if (this.selectedUserRoles.length > 0) {
          params.user_role_filters = this.selectedUserRoles.join(',')
        }

        // Add contract filter if selected
        if (this.selectedContracts.length > 0) {
          params.contract_filters = this.selectedContracts.join(',')
        }

        // Add engagement type filter if selected
        if (this.selectedEngagementTypes.length > 0) {
          params.engagement_type_filters = this.selectedEngagementTypes.join(',')
        }

        // Add channel filter if selected
        if (this.selectedEngagementChannels.length > 0) {
          params.engagement_channel_filters = this.selectedEngagementChannels.join(',')
        }

        // Add topic filter if selected
        if (this.selectedEngagementTopics.length > 0) {
          params.engagement_topic_filters = this.selectedEngagementTopics.join(',')
        }

        // Add InfoTag group filters
        const validGroups = (this.infoTagGroups || []).filter(g =>
          (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
        )
        if (validGroups.length > 0) {
          params.entity_infotag_groups = JSON.stringify({
            mode: this.infoTagGroupsMode,
            groups: validGroups,
          })
        }

        // Only filter by current user's portfolio if "My Practices" is selected
        if (this.portfolioFilter === 'my') {
          const currentUser = this.$store.state.auth.current_user
          if (currentUser && currentUser.id) {
            params.portfolio_user_id = currentUser.id
          }
        }

        // Add entity scope filter if selected
        if (this.selectedEntityId) {
          params.scope_entity_id = this.selectedEntityId
        }

        const response = await axios.get('/api/engagements/attribution-analytics/', { params })

        if (response.data.success) {
          this.coverageData = response.data
          this.transformDataForChart()
        } else {
          this.error = response.data.error || 'Failed to fetch coverage data'
        }
      } catch (error) {
        console.error('Error fetching coverage data:', error)
        this.error = error.response?.data?.error || 'Failed to fetch coverage data'
      } finally {
        this.loading = false
        this.saveFilters()
      }
    },

    transformDataForChart() {
      if (!this.coverageData || !this.coverageData.data || this.coverageData.data.length === 0) {
        this.chartData = null
        return
      }

      const data = this.coverageData.data
      const metric = this.filters.coverage_metric
      const groupBy = this.filters.group_by
      const valueField = metric === 'entity' ? 'entity_coverage_percent' : 'attribution_coverage_percent'

      // Get unique sorted time periods
      const timePeriods = [...new Set(data.map(d => d.observation_date))].sort()
      const labels = timePeriods.map(tp => this.formatDateLabel(tp))

      let series = []

      if (groupBy === 'total') {
        const values = timePeriods.map(tp => {
          const item = data.find(d => d.observation_date === tp)
          return item ? item[valueField] : 0
        })

        const color = metric === 'entity' ? '#4F46E5' : '#10B981'
        series = [{
          values: values,
          config: {
            label: metric === 'entity' ? 'Practice Coverage %' : 'Attribution Coverage %',
            backgroundColor: color,
            type: 'bar',
            yAxis: 'left',
            offset: 0
          }
        }]
      } else {
        // Grouped: build one series per group_value
        const categories = [...new Set(data.map(d => d.group_value))].sort()
        const colorMap = this.colorPalettes[groupBy] || {}

        series = categories.map((category, idx) => {
          const values = timePeriods.map(tp => {
            const item = data.find(d => d.observation_date === tp && d.group_value === category)
            return item ? item[valueField] : 0
          })

          const color = colorMap[category] || this.getColorForIndex(idx)

          return {
            values: values,
            config: {
              label: category || 'Unknown',
              backgroundColor: color,
              type: 'bar',
              yAxis: 'left',
              offset: 0,
              stack: 'main'
            }
          }
        })
      }

      const showLegend = groupBy !== 'total'

      this.chartConfig = {
        chartType: 'vertical_bar',
        title: this.getChartTitle(),
        margin: { top: 80, right: showLegend ? 160 : 60, bottom: 120, left: 80 },
        xAxis: {
          labelRotation: -45,
          fontSize: 12,
          tickFormat: null
        },
        yAxes: [
          {
            position: 'left',
            fontSize: 12,
            tickFormat: (d) => `${d}%`
          }
        ],
        legend: {
          display: showLegend,
          position: 'right',
          spacing: 15
        },
        formatting: {
          percentage: false,
          sig_figs: 1
        },
        labelDisplayConfig: {
          maxLabels: 40,
          minLabelSpacing: 5,
          labelFontSize: 12
        }
      }

      this.chartData = {
        labels: labels,
        series: series
      }
    },

    getColorForIndex(idx) {
      const colors = [
        '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
        '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#14B8A6',
        '#6366F1', '#A855F7', '#D946EF', '#0EA5E9', '#22C55E'
      ]
      return colors[idx % colors.length]
    },

    formatDateLabel(dateStr) {
      if (!dateStr) return 'Invalid Date'

      const date = new Date(dateStr + 'T00:00:00Z')

      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }

      const granularity = this.filters.observation_granularity

      switch(granularity) {
        case 'day':
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })
        case 'week':
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })
        case 'month':
          return date.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC'
          })
        default:
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    navigateToEntity(entityId) {
      this.$router.push(`/entity/${entityId}/`)
    },

    exportToExcel() {
      if (!this.coverageData) {
        console.warn('No coverage data available to export')
        return
      }
      const counted = (this.coverageData.practice_details?.practices_counted || []).map(row => ({
        Category: 'Counted',
        'Entity Name': row.entity_name,
        Date: this.formatDate(row.date),
        Subject: row.subject || '',
        Channel: row.channel || '',
        Type: row.type || '',
        Status: row.status || '',
        'Last Engagement Date': '',
        'Days Since Engagement': '',
      }))
      const notCounted = (this.coverageData.practice_details?.practices_not_counted || []).map(row => ({
        Category: 'Not Counted',
        'Entity Name': row.entity_name,
        Date: '',
        Subject: '',
        Channel: '',
        Type: '',
        Status: '',
        'Last Engagement Date': row.last_engagement_date ? this.formatDate(row.last_engagement_date) : 'Never',
        'Days Since Engagement': row.days_since_engagement ?? '',
      }))
      const rows = [...counted, ...notCounted]
      if (!rows.length) {
        console.warn('No practice details to export')
        return
      }
      const summary = this.coverageData.summary || {}
      const summaryRows = [
        {},
        { Category: '--- SUMMARY ---' },
        { Category: 'Practice Coverage %', 'Entity Name': summary.entity_coverage_percent },
        { Category: 'Practices With Engagement', 'Entity Name': summary.entities_with_engagement },
        { Category: 'Total Practices', 'Entity Name': summary.total_entities },
        { Category: 'Attribution Coverage %', 'Entity Name': summary.attribution_coverage_percent },
        { Category: 'Attribution With Engagement', 'Entity Name': summary.attribution_with_engagement },
        { Category: 'Total Attribution', 'Entity Name': summary.total_attribution },
        { Category: 'Date Range', 'Entity Name': `${this.filters.start_date} to ${this.filters.end_date}` },
        { Category: 'Scope', 'Entity Name': this.portfolioFilter === 'my' ? 'My Practices' : 'All Practices' },
        { Category: 'Status Filters', 'Entity Name': this.selectedStatuses.join(', ') || 'All' },
        { Category: 'Group By', 'Entity Name': this.filters.group_by },
      ]
      const filename = `engagement_coverage_${new Date().toISOString().split('T')[0]}.xlsx`
      exportToExcel([...rows, ...summaryRows], filename)
    }
  },

  watch: {
    'filters.coverage_metric': {
      handler() {
        this.transformDataForChart()
      }
    },
    portfolioFilter() {
      this.selectedEntityId = null
      this.fetchScopeEntities()
      this.fetchCoverageData()
    }
  },

  async mounted() {
    // Load saved filters before setting defaults
    this.loadFilters()

    // Only set default portfolio if not loaded from storage
    if (!this.portfolioFilter) {
      this.portfolioFilter = this.tabs[0].id
    }

    await Promise.all([
      this.fetchUserRoleOptions(),
      this.fetchContracts(),
      this.fetchEngagementChannels(),
      this.fetchEngagementTypes(),
      this.fetchEngagementTopics(),
      this.fetchInfoTags(),
      this.fetchScopeEntities()
    ])
    await this.fetchCoverageData()
  }
}
</script>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
